import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import PhoneInput from '../components/PhoneInput';
import './ProfilePage.css';

function FAQItem({ question, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <span className={`faq-chevron ${open ? 'open' : ''}`}>&rsaquo;</span>
      </button>
      {open && <div className="faq-answer">{children}</div>}
    </div>
  );
}

function FAQTab() {
  return (
    <div className="faq-container">
      <FAQItem question="Who are we?">
        <p>The Daily Bid is an independent luxury auction agent based in Europe. We source authenticated vintage luxury goods from Japan's most reputable auction houses and deliver them directly to you.</p>
        <p>We act as your personal bidding agent — we handle sourcing, bidding, authentication, customs, shipping and delivery so you can focus on finding the perfect piece.</p>
      </FAQItem>

      <FAQItem question="How do you guarantee authenticity?">
        <p>Every item purchased through The Daily Bid is carefully inspected by our team of luxury goods experts before delivery. Each purchase includes an authenticity certificate issued by an independent technology solution (LegitApp).</p>
        <p>In the unlikely event that an item is found to be non-authentic, you will receive a full refund — no questions asked.</p>
      </FAQItem>

      <FAQItem question="How does shipping work?">
        <p>Once you win an auction, we handle the entire logistics chain: collection from the Japanese auction house, international shipping, customs clearance and import duties.</p>
        <p>Your item is fully insured throughout the journey and delivered door-to-door. Delivery typically takes 2–4 weeks after the auction closes.</p>
        <p>Shipping costs are included in the price you see — there are no hidden fees.</p>
      </FAQItem>

      <FAQItem question="How is the price calculated?">
        <p>The price you see is all-inclusive — shipping, customs, duties, authentication and all taxes are included. There are no hidden fees.</p>
        <p>Our service is subject to a commission on each winning bid, which is already factored into the price displayed. The commission rate varies depending on the price range.{' '}
          <button className="faq-contact-link" style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', padding: 0 }} onClick={() => { const el = document.querySelector('.footer-link'); if (el) el.click(); }}>View our fee schedule</button>.
        </p>
      </FAQItem>

      <FAQItem question="How does payment work?">
        <p>Once you win an auction, our team will contact you with payment instructions. We accept bank transfers and major credit cards.</p>
        <p>Payment is due within 48 hours of the auction result confirmation. Your item will be shipped once payment is received and authentication is complete.</p>
      </FAQItem>

      <FAQItem question="How can I contact you?">
        <p>You can reach us anytime via WhatsApp for quick questions about a product, your order, or anything else.</p>
        <p><a href="#" className="faq-contact-link">Chat with us on WhatsApp</a></p>
        <p>You can also email us at <a href="mailto:welcome@thedailybid.com" className="faq-contact-link">welcome@thedailybid.com</a></p>
      </FAQItem>
    </div>
  );
}

export default function ProfilePage() {
  const { logout } = useAuth();
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => api.get('/users/me').then(r => r.data) });
  const [activeTab, setActiveTab] = useState('profile');
  const [form, setForm] = useState({});
  const [pwd, setPwd] = useState({ current_password: '', new_password: '' });
  const [msg, setMsg] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  const updateProfile = useMutation({
    mutationFn: () => api.patch('/users/me', form),
    onSuccess: () => setMsg('Profile updated'),
    onError: err => setMsg(err.response?.data?.detail || 'Error'),
  });

  const changePwd = useMutation({
    mutationFn: () => api.post('/users/change-password', pwd),
    onSuccess: () => { setPwdMsg('Password changed'); setPwd({ current_password: '', new_password: '' }); },
    onError: err => setPwdMsg(err.response?.data?.detail || 'Error'),
  });

  if (!user) return null;

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 6 };

  return (
    <div className="profile-page">
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 400, marginBottom: 24 }}>Profile</h1>
      <div className="profile-tabs">
        <button className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>Account</button>
        <button className={`profile-tab ${activeTab === 'faq' ? 'active' : ''}`} onClick={() => setActiveTab('faq')}>FAQ</button>
      </div>

      {activeTab === 'faq' && <FAQTab />}

      {activeTab === 'profile' && <div style={{ maxWidth: 500 }}>
        <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 32, marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Personal information</h2>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label style={labelStyle}>Email</label>
              <input className="input" value={user.email || ''} disabled readOnly style={{ background: '#F9F8F4', color: 'var(--color-text-muted)', cursor: 'not-allowed' }} />
            </div>
            {[['prenom', 'First name', user.prenom], ['nom', 'Last name', user.nom], ['adresse', 'Address', user.adresse || '']].map(([key, label, val]) => (
              <div key={key} className="form-group">
                <label style={labelStyle}>{label}</label>
                <input className="input" defaultValue={val} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
              </div>
            ))}
            <div className="form-group">
              <label style={labelStyle}>Phone</label>
              <PhoneInput
                value={form.telephone !== undefined ? form.telephone : (user.telephone || '')}
                onChange={val => setForm(f => ({ ...f, telephone: val }))}
              />
            </div>
          </div>
          {msg && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--color-success)' }}>{msg}</div>}
          <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => updateProfile.mutate()}>Save changes</button>
        </div>

        <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 32, marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Change password</h2>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label style={labelStyle}>Current password</label>
              <div style={{ position: 'relative' }}>
                <input className="input" type={showCurrentPwd ? 'text' : 'password'} value={pwd.current_password} onChange={e => setPwd(p => ({ ...p, current_password: e.target.value }))} style={{ paddingRight: 40 }} />
                <button type="button" onClick={() => setShowCurrentPwd(!showCurrentPwd)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 13 }}>{showCurrentPwd ? 'Hide' : 'Show'}</button>
              </div>
            </div>
            <div className="form-group">
              <label style={labelStyle}>New password</label>
              <div style={{ position: 'relative' }}>
                <input className="input" type={showNewPwd ? 'text' : 'password'} value={pwd.new_password} onChange={e => setPwd(p => ({ ...p, new_password: e.target.value }))} style={{ paddingRight: 40 }} />
                <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 13 }}>{showNewPwd ? 'Hide' : 'Show'}</button>
              </div>
            </div>
          </div>
          {pwdMsg && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--color-success)' }}>{pwdMsg}</div>}
          <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => changePwd.mutate()}>Change password</button>
        </div>

        <button className="btn" style={{ width: '100%', background: 'var(--color-accent)', color: 'white', border: 'none' }} onClick={logout}>Sign out</button>
      </div>}
    </div>
  );
}
